import { zodResolver } from "@hookform/resolvers/zod";
import {
  OrganizationProductType,
  Product,
  ProductProductionRateType,
  ProductType,
} from "generated/graphql";
import { FormProvider, useForm } from "react-hook-form";
import {
  getDefaultValues,
  getProductUpdateSchema,
  getProductUpdateSchemaSubcontracted,
  getProductUpdateSchemaUnitPrice,
} from "DDD/action-objects/ProductUpdate";
import { Card, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import { SelectOptions } from "components/Shared/CustomSelect/CustomSelect";
import { evolve } from "ramda";
import MDButton from "components/MDButton";
import { cleanNullValues } from "utils/object/cleanNullValues";
import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { getRoute } from "utils/routing";
import ProductFormFields from "./ProductFormFields";
import { z } from "zod";
import { concreteProducts, requiresPlant } from "DDD/action-objects/ProductCreate";

type ProductUpdateFormContentProps = {
  onSubmit: any;
  loading: any;
  materialCostTypeOptions: SelectOptions;
  labourCostTypeOptions: SelectOptions;
  productType: OrganizationProductType;
  isSubcontracted: boolean;
  setIsSubcontracted: React.Dispatch<React.SetStateAction<boolean>>;
  isUnitPrice: boolean;
  setIsUnitPrice: React.Dispatch<React.SetStateAction<boolean>>;
  canBeMeasuredInTons: boolean;
  productRequiresPlant: boolean;
  schema: z.ZodTypeAny;
  defaultValues: Record<string, unknown>;
};

function ProductUpdateFormContent({
  onSubmit,
  loading,
  materialCostTypeOptions,
  labourCostTypeOptions,
  productType,
  isSubcontracted,
  setIsSubcontracted,
  isUnitPrice,
  setIsUnitPrice,
  canBeMeasuredInTons,
  productRequiresPlant,
  schema,
  defaultValues,
}: ProductUpdateFormContentProps) {
  const navigate = useNavigate();
  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const { handleSubmit: zodHandleSubmit, reset } = methods;

  const handleSubmit = useCallback(
    async ({ plantId, ...values }) => {
      const preparedValues = evolve({
        attributes: (attrs) => (attrs ? cleanNullValues(attrs) : attrs),
      })({
        ...values,
        subcontracted: values?.subcontracted ?? false,
        unitPriceProduct: values?.unitPriceProduct ?? false,
        ...(productRequiresPlant && {
          productionRateType: values?.productionRateType
            ? ProductProductionRateType.TONS_PER_HOUR
            : ProductProductionRateType.SQ_FT_SQ_YD_PER_HOUR,
        }),
        equipmentPieces: {
          sync: values?.equipmentPieces?.sync?.map(({ id, quantity }) => ({ id, quantity })),
        },
        additionalCosts: {
          sync: values?.additionalCosts?.sync
            ?.filter(({ id }) => id)
            ?.map(({ id, quantity }) => ({ id, quantity })),
        },
        crews: {
          sync:
            values?.crews?.sync
              ?.filter(({ id }) => id)
              ?.map(({ id, quantity }) => ({ id, quantity })) ?? [],
        },
        plantProducts: {
          sync: values?.plantProducts?.sync
            ?.filter(({ plantId, id }) => plantId && id)
            ?.map(({ plantId, id, applicationRate }) => ({ plantId, id, applicationRate })),
        },
        materialsNoPlant: {
          upsert:
            !productRequiresPlant && values?.materialsNoPlant
              ? values.materialsNoPlant.filter(
                  (material) => material.name && material.name.trim() !== ""
                )
              : [],
        },
      });

      if (isSubcontracted) {
        delete preparedValues.equipmentPieces;
        delete preparedValues.crews;
        delete preparedValues.additionalCosts;
      }
      if (isUnitPrice) {
        delete preparedValues.equipmentPieces;
      }

      if (productType.type !== ProductType.GENERIC) {
        delete preparedValues.areaCoverage;
      }

      if (!preparedValues.plantProducts) {
        preparedValues.plantProducts = { sync: [] };
      }

      const result = await onSubmit(preparedValues);
      if (result.success) {
        reset();
        navigate(getRoute("product-types.products.list", [["productTypeId", productType.id]]));
      }
    },
    [
      isSubcontracted,
      isUnitPrice,
      navigate,
      onSubmit,
      productRequiresPlant,
      productType.id,
      productType.type,
      reset,
    ]
  );

  return (
    <FormProvider {...methods}>
      <MDBox
        p={3}
        component="form"
        role="form"
        id="product-update-form"
        onSubmit={(e) => {
          if (e.currentTarget.id === "product-update-form") {
            zodHandleSubmit(handleSubmit)(e);
          } else {
            e.preventDefault();
          }
        }}
      >
        <ProductFormFields
          action="update"
          materialCostTypeOptions={materialCostTypeOptions}
          labourCostTypeOptions={labourCostTypeOptions}
          productType={productType}
          isSubcontracted={isSubcontracted}
          isUnitPrice={isUnitPrice}
          setIsUnitPrice={setIsUnitPrice}
          setIsSubcontracted={setIsSubcontracted}
          canBeMeasuredInTons={canBeMeasuredInTons}
        />
        <Grid item xs={12} display="flex" justifyContent="flex-end">
          <MDButton
            type="submit"
            variant="gradient"
            color="success"
            disabled={loading}
            form="product-update-form"
            name="product-update-form"
          >
            Update Product
          </MDButton>
        </Grid>
      </MDBox>
    </FormProvider>
  );
}

export default function OrganizationProductTypeProductUpdateForm({
  onSubmit,
  loading,
  materialCostTypeOptions,
  labourCostTypeOptions,
  productType,
  product,
  isUnitPrice,
  setIsUnitPrice,
  isSubcontracted,
  setIsSubcontracted,
}: {
  onSubmit: any;
  loading: any;
  materialCostTypeOptions: SelectOptions;
  labourCostTypeOptions: SelectOptions;
  productType: OrganizationProductType;
  product: Product;
  isSubcontracted: boolean;
  setIsSubcontracted: React.Dispatch<React.SetStateAction<boolean>>;
  isUnitPrice: boolean;
  setIsUnitPrice: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const productRequiresPlant = requiresPlant.includes(ProductType[productType.type]);
  const isConcreteProduct = concreteProducts.includes(productType.type as ProductType);
  const canBeMeasuredInTons = productRequiresPlant && !isConcreteProduct;

  const schema = useMemo(() => {
    if (isSubcontracted) {
      return getProductUpdateSchemaSubcontracted;
    }
    if (isUnitPrice) {
      return getProductUpdateSchemaUnitPrice({ productType: productType.type as ProductType });
    }
    return getProductUpdateSchema({ productType: productType.type as ProductType });
  }, [isSubcontracted, isUnitPrice, productType.type]);

  const defaultValues = useMemo(
    () =>
      getDefaultValues({
        ...product,
        organizationProductTypeId: productType.id,
        productType: productType.type as ProductType,
        ...(canBeMeasuredInTons && {
          productionRateType:
            product.productionRateType === ProductProductionRateType.TONS_PER_HOUR,
        }),
      }),
    [canBeMeasuredInTons, product, productType.id, productType.type]
  );

  return (
    <Card>
      <ProductUpdateFormContent
        key={`${isUnitPrice}-${isSubcontracted}`}
        onSubmit={onSubmit}
        loading={loading}
        materialCostTypeOptions={materialCostTypeOptions}
        labourCostTypeOptions={labourCostTypeOptions}
        productType={productType}
        isSubcontracted={isSubcontracted}
        setIsSubcontracted={setIsSubcontracted}
        isUnitPrice={isUnitPrice}
        setIsUnitPrice={setIsUnitPrice}
        canBeMeasuredInTons={canBeMeasuredInTons}
        productRequiresPlant={productRequiresPlant}
        schema={schema}
        defaultValues={defaultValues}
      />
    </Card>
  );
}
